import { type SettingsStructureSetting } from "@client/settings/structure";
import {
  BackgroundImagePosition,
  BackgroundImageRepeat,
  BackgroundImageSize,
  BackgroundType,
  HeadingLevel,
  type SortOrder
} from "@client/types";
import { SyntaxType } from "@common/controls/Inputs/HighlightEditor/component";
import { type Property } from "csstype";
import { type GenericMap } from "./GenericMap";
import { type StyleSheetColorType, type StyleSheetTextType } from "./StyleSheet";

export type Availability =
  | string
  | {
      disabled?: string;
      visible?: string;
    };

export enum SettingDefinitionMeta {
  HostOnly = "host"
}

export enum CheckboxType {
  OnOff = "onOff"
}

export enum SidesType {
  Border = "border",
  Margin = "margin",
  Padding = "padding"
}

export interface WithSubType {
  subType: string;
}

export type CheckboxSettingDefinitionOptions = CommonSettingDefinitionOptions &
  WithSubType & {
    linkText?: string;
    moduleType?: string;
  };
export type CheckboxOnOffSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  onText?: string;
  offText?: string;
};
export type ColorSettingDefinitionOptions = CommonSettingDefinitionOptions;

export type CornersSettingDefinitionOptions = CommonSettingDefinitionOptions & WithSubType;
export type DateRangeSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  minDate?: string | null;
  maxDate?: string | null;
};
export type DateTimeSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  canClear?: boolean;
  isToday?: boolean;
  showTime?: boolean;
};
export type SidesSettingDefinitionOptions = CommonSettingDefinitionOptions &
  WithSubType & {
    min?: number;
    max: number;
    placeholder?: string;
  };

export type HighlightEditorSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  syntax: SyntaxType | string;
};

export type WidthHeightSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  isNoHug?: boolean;
};

export type TextInputType = "email" | "number" | "text" | "password";
export type TextSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  multiline?: boolean;
  placeholder?: string;
  rows?: number;
  syntax?: SyntaxType | string;
  type?: TextInputType;
  onBlur?: () => void;
};

export type ListSettingNoValue =
  | {
      hasSeparator?: boolean;
      label?: string;
      value?: string;
    }
  | string;
export type ListSettingDefinitionOptions = CommonSettingDefinitionOptions &
  WithSubType & {
    items?: OptionItem[] | string /* array or endpoint url to fetch items from */;
    placeholder?: string;
    sort?: "asc" | "desc";
    noValue?: ListSettingNoValue;
    selectAllLabel?: string;
    uniqueValues?: any[] /* defines values of multi select that one can be selected at a time */;
  };

export type NumberSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  addon?: any;
  allowEmpty?: boolean;
  min?: number;
  max?: number;
  fractionalDigits?: number;
  step?: number;
  placeholder?: string;
};

export type RangeSettingStrict = "max" | "min" | boolean;
export type RangeSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  allowEmpty?: boolean;
  hasAuto?: boolean;
  defaultUnit?: string;
  fixedMax?: number;
  min: number;
  max: number;
  placeholder?: string;
  step?: number;
  strict?: RangeSettingStrict;
  units?: Array<OptionItem | string>;
};

export type MeasureSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  hasAuto?: boolean;
  def?: string;
  units?: Array<OptionItem | string>;
};

export interface CommonSettingDefinitionOptions {
  mode?: string;
  subType?: string;
}

export type FileSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  accept: string;
  type: string;
};

export type FontSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  subType: string;
  placeholder: string;
};

export type LinkedSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  items: string;
};

export type ModuleItemsSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  addTooltip?: string;
  idProp?: string;
  items?: string | Array<OptionItem | SeparatorOptionItem>;
  labelProp?: string;
  sort?: SortOrder;
  type?: string;
  uniqueValues?: any[] /* defines item values that cannot be selected more than once */;
};

export type RedirectToSettingDefinitionOptions = CommonSettingDefinitionOptions & {};

export type StyleSheetColorSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  type: StyleSheetColorType;
};
export type StyleSheetHeadingsSettingDefinitionOptions = CommonSettingDefinitionOptions;
export type StyleSheetTextSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  type: StyleSheetTextType;
};
export type StyleSheetLinkSettingDefinitionOptions = CommonSettingDefinitionOptions;

export type QueryStringSettingDefinitionOptions = CommonSettingDefinitionOptions & {
  urlProp?: string;
};

export type SettingDefinitionOptions =
  | ColorSettingDefinitionOptions
  | DateRangeSettingDefinitionOptions
  | DateTimeSettingDefinitionOptions
  | FileSettingDefinitionOptions
  | FontSettingDefinitionOptions
  | HighlightEditorSettingDefinitionOptions
  | MeasureSettingDefinitionOptions
  | NumberSettingDefinitionOptions
  | TextSettingDefinitionOptions
  | RangeSettingDefinitionOptions
  | ListSettingDefinitionOptions
  | LinkedSettingDefinitionOptions
  | ModuleItemsSettingDefinitionOptions
  | CornersSettingDefinitionOptions
  | SidesSettingDefinitionOptions
  | CheckboxSettingDefinitionOptions
  | CheckboxOnOffSettingDefinitionOptions
  | RedirectToSettingDefinitionOptions
  | QueryStringSettingDefinitionOptions
  | StyleSheetColorSettingDefinitionOptions
  | StyleSheetHeadingsSettingDefinitionOptions
  | StyleSheetLinkSettingDefinitionOptions
  | StyleSheetTextSettingDefinitionOptions
  | WidthHeightSettingDefinitionOptions;

export interface SettingDefinition {
  label?: string;
  help?: string;
  type: string;
  inheritFrom?: string;
  opts?: SettingDefinitionOptions;
  def?: any;
  span?: number;
  meta?: string[] | SettingDefinitionMeta[];
  noValue?: any /* when we don't need to store setting's default value, this should be used to be displayed in the UI */;
  expanded?: boolean /* some settings should never be collapsed (responsive to simple value). TODO: think of a better solution */;
  source?: string; // optional mapping to a prop
  static?: boolean /* true - not responsive */;
  dependencies?: string[];

  /**
   * inside a mandatory accordion (name start with >), it will be possible to hide the setting. The value is used when it's put back
   */
  optional?: any;
  shorthand?: SettingDefinitionShorthand;
}

export type SettingDefinitionShorthand = {
  def?: string;
  format: string;
  structure: GenericMap<SettingsStructureSetting>;
  noValue?: string;
};

export type SettingCompositeSetting = {
  definitionName?: string;
  availability?: Availability;
};

export type SettingsDefinitionTree = GenericMap<SettingDefinition>;

export type LocalSettings = GenericMap<any> | null | undefined;
export type MainSettings = GenericMap<any>;

export interface ImageValue {
  original: string;
  resized: string;
}

export interface OptionItem<TValue = any> {
  group?: string;
  label: string;
  image?: any;
  value: TValue;
  parentValue?: any;
  sortOrder?: number;

  children?: OptionItem[];
}

export type SeparatorOptionItem = {};

export interface WithOptions<T = OptionItem> {
  options: T[];
}

export interface ImageSettingValue {
  original: string;
  resized: string;
  download: string;
}

export type SettingValue = string | number | boolean | undefined | null | ImageSettingValue | object;

export namespace CompositeSettings {
  export type Background = {
    type: BackgroundType;
    color?: string;
    image?: string;
    imageSize?: BackgroundImageSize;
    imageFixed?: boolean;
    imageHeight?: string;
    imagePosition?: BackgroundImagePosition;
    imageRepeat?: BackgroundImageRepeat;
    imageWidth?: string;
    imageParallax?: string;
    overlay?: Overlay | null;
    opacity?: string;
    video?: string;
    videoYouTubeID?: string;
  };

  export type BackgroundHover = Omit<Background, "overlay"> & {
    hoverColor?: string;
    overlay?: OverlayHover | null;
  };

  export type Border = {
    radius?: string;
    color?: string;
    style: string;
    width: string;
  };

  export type BorderHover = Border & {
    hoverColor: string;
  };

  export type Overlay = {
    color: string;
    opacity: string;
  };

  export type OverlayHover = Overlay & {
    hoverColor?: string;
  };

  export type Heading = {
    font: string;
    size: string;
    letter: string;
    line: string;
    weight: number;

    color: string | null | undefined;
    s_text: string | null | undefined;
  };

  export type HeadingExtended = Heading & {
    decoration: TextDecoration | null;
    shadow: TextShadow | null;
    style: "italic" | "normal" | "oblique" | null;
    transform: TextTransform;
  };

  export type Image = ImageSettings & {
    alt?: string | null;
    src: string | null;
  };

  export type ImageSettings = {
    fit: "unset" | "contain" | "cover" | "fill" | "scale-down";
    position: string;
    size: string;
    border: Border | null;
    margin: string;
    shadow: Shadow | null;
  };

  export type Position = {
    type: "relative" | "absolute" | "sticky" | "fixed";
    position?: string;
    zIndex?: number;
  };

  export type Link = {
    color: string | undefined;
    decoration: "underline" | undefined;

    hover?: {
      color: string | undefined;
      decoration: "underline" | undefined;
    };
  };

  export type TextTransform = "capitalize" | "full-size-kana" | "full-width" | "lowercase" | "uppercase" | "small-caps";
  export type Text = HeadingExtended & {
    s_text?: string;
  };

  export type TextHover = Text & {
    hoverColor: string;
  };

  export type HText = Text & {
    level: HeadingLevel | undefined;
  };

  export type Shadow = TextShadow & {
    spread: string;
  };

  export type TextShadow = {
    x: string;
    y: string;
    blur: string;
    color: string;
  };

  export type TextDecoration = {
    type: Property.TextDecorationLine;
    line: Property.TextDecorationStyle;
    color: string;
  };

  export type Logo = {
    position: "left" | "center" | "right";
    size: string;
    type: "system" | "custom";
    file: string | null;
    padding: string;
  };

  export type RedirectToType = "popup" | "step" | "tab" | "url" | "lightbox";

  export type Redirect = {
    newTab?: boolean;
    to: CompositeSettings.RedirectToType;
    funnelPath?: string | null;
    page?: string | null;
    popup?: string | null;
    queryString?: string | null;
    url?: string | null;
    useReturnUrl?: string;
  };
}
